import PropTypes from 'prop-types';
import React from 'react';
import { Box, Divider, Paper, Typography } from '@mui/material';

import colors from 'common/constants/colors';

/**
 * The shell both association cards sit in: a title, an optional header control, a scrollable list,
 * and a footer pinned to the bottom.
 *
 * Written once so the two cards cannot drift apart. They are side by side and read as a pair, so a
 * different padding or a differently placed filter on one of them looks like a bug rather than a
 * choice.
 *
 * The list scrolls inside the card rather than growing it, which keeps both cards the same height
 * and the two Add rows on the same line — a sig with thirty directions beside one with two would
 * otherwise leave the second card's footer floating halfway up the dialog.
 */
const AssociationCard = ({ title, headerAction, footer, children }) => (
  <Paper
    elevation={0}
    // A named region, so the two cards are separately addressable. Both hold a Status filter and an
    // Add button, and without a name on the card the only way to tell one from the other is
    // position — which is no use to a screen reader and no use to a test.
    component='section'
    aria-label={title}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      borderRadius: 2,
      border: `1px solid ${colors.cardBorderGrey}`,
      bgcolor: colors.white,
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        px: 2.5,
        py: 2,
      }}
    >
      <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {headerAction}
    </Box>

    <Divider />

    <Box sx={{ flex: '1 1 auto', overflowY: 'auto', px: 2.5, py: 1.5, minHeight: 180 }}>
      {children}
    </Box>

    {footer && (
      <>
        <Divider />
        <Box sx={{ px: 2.5, py: 2, bgcolor: colors.dialogSurfaceGrey }}>{footer}</Box>
      </>
    )}
  </Paper>
);

export default AssociationCard;

AssociationCard.propTypes = {
  title: PropTypes.string.isRequired,
  headerAction: PropTypes.node,
  footer: PropTypes.node,
  children: PropTypes.node,
};

AssociationCard.defaultProps = {
  headerAction: undefined,
  footer: undefined,
  children: undefined,
};
